import { Select, Switch } from '@mantine/core';
import { IconAnchor, IconClock, IconRosetteDiscountCheck, IconShip } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import lib from '../pms-library/PmsLibrary.module.css';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import classes from './Drydock.module.css';
import type { DrydockProjectSource, DrydockScopeSource } from './source.js';
import type { DrydockJobListRow, DrydockProject, DrydockProjects } from './types.js';

export interface DrydockFleetScreenProps {
  projects: DrydockProjectSource;
  jobs: DrydockScopeSource;
}

const STATE_TONE: Record<DrydockProject['state'], StatusTone> = {
  planning: 'info',
  in_yard: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

const ALL = 'all';
const live = (p: DrydockProject) => p.state === 'planning' || p.state === 'in_yard';

export interface FleetRow {
  project: DrydockProject;
  jobs: number;
  awaiting: number;
  inProgress: number;
  verification: number;
  pending: number;
}

/** Proje başına iş sayıları (iptal edilen iş sayılmaz; taşınan Pending iş sayılmaz). */
export function fleetRows(
  projects: readonly DrydockProject[],
  jobs: readonly DrydockJobListRow[],
  showAll: boolean,
): FleetRow[] {
  return projects
    .filter((p) => showAll || live(p))
    .map((project) => {
      const own = jobs.filter((j) => j.projectId === project.id && j.state !== 'cancelled');
      const state = (j: DrydockJobListRow) => j.execution?.state ?? 'not_started';
      return {
        project,
        jobs: own.length,
        awaiting: own.filter((j) => j.status === 'for_review').length,
        inProgress: own.filter((j) => state(j) === 'in_progress').length,
        verification: own.filter((j) => state(j) === 'completed').length,
        pending: own.filter((j) => state(j) === 'pending' && !j.carriedTo).length,
      };
    });
}

/** Özet kartları: etkin proje, ofiste bekleyen iş, tersanedeki proje, doğrulama bekleyen iş. */
export function fleetCounts(
  projects: readonly DrydockProject[],
  jobs: readonly DrydockJobListRow[],
): { active: number; awaiting: number; inYard: number; verification: number } {
  const open = jobs.filter((j) => j.state !== 'cancelled');
  return {
    active: projects.filter(live).length,
    awaiting: open.filter((j) => j.status === 'for_review').length,
    inYard: projects.filter((p) => p.state === 'in_yard').length,
    verification: open.filter((j) => j.execution?.state === 'completed').length,
  };
}

const projectPath = (id: string) => `/pms/drydock/projects?project=${encodeURIComponent(id)}`;
const jobsPath = (id: string, filter: string) =>
  `/pms/drydock/jobs?project=${encodeURIComponent(id)}&filter=${filter}`;

/** Filo tablosu: satırdan projeye, sayılardan iş listesinin süzgecine geçilir. */
export function FleetTable({
  rows,
  onNavigate,
}: {
  rows: readonly FleetRow[];
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydockFleet.columns.${key}`);
  if (rows.length === 0) return <EmptyState title={t('drydockFleet.empty')} />;
  const count = (value: number, path: string, column: string, projectNo: string) =>
    value > 0 ? (
      <button
        type="button"
        className={classes.linkButton}
        aria-label={`${k(column)} · ${projectNo}: ${value}`}
        onClick={() => onNavigate(path)}
      >
        {value}
      </button>
    ) : (
      <span className={classes.muted}>0</span>
    );
  return (
    <section
      className={table.tableWrap}
      aria-label={t('drydockFleet.title')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.checkpointTable}`}>
        <thead>
          <tr>
            <th scope="col">{k('vessel')}</th>
            <th scope="col">{k('project')}</th>
            <th scope="col">{k('state')}</th>
            <th scope="col">{k('planned')}</th>
            <th scope="col">{k('inspection')}</th>
            <th scope="col">{k('jobs')}</th>
            <th scope="col">{k('awaiting')}</th>
            <th scope="col">{k('inProgress')}</th>
            <th scope="col">{k('verification')}</th>
            <th scope="col">{k('pending')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const p = row.project;
            const last = p.inspections.at(-1);
            return (
              <tr key={p.id}>
                <td>{p.vesselName ?? '—'}</td>
                <td>
                  <button
                    type="button"
                    className={`${classes.linkButton} ${classes.mono}`}
                    onClick={() => onNavigate(projectPath(p.id))}
                  >
                    {p.projectNo}
                  </button>
                  <div className={classes.muted}>{p.title}</div>
                </td>
                <td>
                  <StatusBadge tone={STATE_TONE[p.state]}>
                    {t(`drydock.states.${p.state}`)}
                  </StatusBadge>
                </td>
                <td className={classes.mono}>
                  {p.plannedStart || p.plannedEnd
                    ? `${formatDay(p.plannedStart)} – ${formatDay(p.plannedEnd)}`
                    : '—'}
                </td>
                <td className={classes.mono}>
                  {last
                    ? `${last.inspectionNo} · ${t(`drydock.inspectionStates.${last.state}`)}`
                    : '—'}
                </td>
                <td>{count(row.jobs, jobsPath(p.id, 'all'), 'jobs', p.projectNo)}</td>
                <td>{count(row.awaiting, jobsPath(p.id, 'awaiting'), 'awaiting', p.projectNo)}</td>
                <td>{count(row.inProgress, jobsPath(p.id, 'yard'), 'inProgress', p.projectNo)}</td>
                <td>
                  {count(
                    row.verification,
                    jobsPath(p.id, 'verification'),
                    'verification',
                    p.projectNo,
                  )}
                </td>
                <td>{count(row.pending, jobsPath(p.id, 'pending'), 'pending', p.projectNo)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; projects: DrydockProjects; jobs: DrydockJobListRow[] };

/**
 * Maintenance (PMS) ▸ Dry Docking ▸ Fleet Overview (ADR 0129 Ek Faz 5; yalnız ofis): filonun
 * havuzlama projeleri, denetim durumu, ofiste bekleyen işler, tersane ilerlemesi ve doğrulama
 * bekleyenler; gemi süzgeci (`?vessel=`). Gemi verisi salt okunurdur; satırlar Projects ve Yard
 * Job List'e derin bağlantıyla açılır.
 */
export function DrydockFleetScreen({ projects, jobs }: DrydockFleetScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const [query, setQuery] = usePmsQuery();
  const vesselId = query.get('vessel');
  const showAll = query.get('all') === '1';
  const [state, setState] = useState<State>({ kind: 'loading' });

  const load = useCallback(async () => {
    try {
      const [list, jobList] = await Promise.all([projects.load(vesselId), jobs.jobs({ vesselId })]);
      setState({ kind: 'ready', projects: list, jobs: jobList.jobs });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [projects, jobs, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <PageHeader title={t('drydockFleet.title')} description={t('drydockFleet.description')} />
  );
  if (state.kind === 'loading') {
    return (
      <div className={lib.screen}>
        {header}
        <TableSkeleton columns={10} label={t('drydockFleet.title')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={lib.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('drydockFleet.error')}
            onRetry={() => {
              setState({ kind: 'loading' });
              void load();
            }}
          />
        </Panel>
      </div>
    );
  }

  const list = state.projects.projects;
  const counts = fleetCounts(list, state.jobs);
  const s = (key: string) => t(`drydockFleet.stats.${key}`);
  return (
    <div className={lib.screen}>
      {header}
      <div className={classes.filters}>
        <Select
          label={t('drydockFleet.vessel')}
          data={[
            { value: ALL, label: t('drydockFleet.allVessels') },
            ...(state.projects.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
          ]}
          value={vesselId ?? ALL}
          allowDeselect={false}
          onChange={(value) => setQuery({ vessel: value === ALL ? null : value })}
        />
        <Switch
          label={t('drydockFleet.showCompleted')}
          checked={showAll}
          onChange={(e) => setQuery({ all: e.currentTarget.checked ? '1' : null })}
        />
      </div>
      <div className={classes.fleetStats}>
        <StatTile
          icon={<IconShip size={20} />}
          label={s('active')}
          value={counts.active}
          wrapLabel
        />
        <StatTile
          icon={<IconClock size={20} />}
          label={s('awaiting')}
          value={counts.awaiting}
          tone={counts.awaiting > 0 ? 'warning' : 'default'}
          wrapLabel
        />
        <StatTile
          icon={<IconAnchor size={20} />}
          label={s('inYard')}
          value={counts.inYard}
          wrapLabel
        />
        <StatTile
          icon={<IconRosetteDiscountCheck size={20} />}
          label={s('verification')}
          value={counts.verification}
          tone={counts.verification > 0 ? 'warning' : 'default'}
          wrapLabel
        />
      </div>
      <Panel flush>
        <FleetTable
          rows={fleetRows(list, state.jobs, showAll)}
          onNavigate={(path) => shell?.navigate(path)}
        />
      </Panel>
    </div>
  );
}
